/**
 * Unit formatting and state→tone mapping shared by every compliance surface.
 *
 * Extracted from `planStatus.ts` (SE-7003) when SE-7006's layer board became
 * the FOURTH surface to need the same two decisions. They are decisions, not
 * conveniences: a magnitude written one way on the Planning tile and another
 * way in the layer card's COMPUTED FIX reads as two different numbers, and a
 * state coloured `warning` in one place and `error` in another reads as two
 * different verdicts. One definition, imported everywhere, is the only way
 * those cannot drift.
 *
 * Nothing here judges. `toneForState` maps a state the SERVER decided; it does
 * not decide one. `formatMagnitude` writes a number the server computed; it
 * does not compute one.
 * @module features/financial-compliance/domain/complianceUnits
 */

import {
  ComplianceQuantityUnit,
  ComplianceRuleState,
  CurrencyCode,
} from "@/generated/graphql";

import formatCurrencyShort from "@/utils/formatCurrencyShort";

import { formatMillionsTight } from "./formatImpact";

import type { StatusPillTone } from "@/components/atoms/StatusPill";

/** The threshold above which a money magnitude is written in whole millions. */
const ONE_MILLION = 1_000_000;

/**
 * Worst-first ordering; the two unjudgeable states rank last, together.
 *
 * `PARTIAL` and `NOT_CONFIGURED` are reports that inputs are missing, not
 * verdicts — a rule nobody has configured is UNMEASURED, which is neither
 * compliant nor breaching.
 */
export const RULE_STATE_SEVERITY: Readonly<
  Record<ComplianceRuleState, number>
> = {
  [ComplianceRuleState.Red]: 3,
  [ComplianceRuleState.Amber]: 2,
  [ComplianceRuleState.Green]: 1,
  [ComplianceRuleState.Partial]: 0,
  [ComplianceRuleState.NotConfigured]: 0,
};

/** Tone per judged state; the unjudgeable states are neutral. */
const TONE: Readonly<Record<ComplianceRuleState, StatusPillTone>> = {
  [ComplianceRuleState.Red]: "error",
  [ComplianceRuleState.Amber]: "warning",
  [ComplianceRuleState.Green]: "success",
  [ComplianceRuleState.Partial]: "neutral",
  [ComplianceRuleState.NotConfigured]: "neutral",
};

/**
 * The tone a rule's marker carries.
 * @param state - The state the server assigned the rule.
 * @returns The pill/dot tone.
 */
export const toneForState = (state: ComplianceRuleState): StatusPillTone =>
  TONE[state];

/**
 * Whether a state is a verdict rather than a report of missing inputs.
 * @param state - The rule state.
 * @returns True for GREEN, AMBER and RED.
 */
export const isJudgeableState = (state: ComplianceRuleState): boolean =>
  RULE_STATE_SEVERITY[state] > 0;

/**
 * A magnitude as a bare number, dropping a trailing `.0`.
 * @param magnitude - The value to write.
 * @returns The number, with one decimal only when it is not whole.
 */
const trimmedOf = (magnitude: number): string =>
  Number.isInteger(magnitude) ? String(magnitude) : magnitude.toFixed(1);

/**
 * A magnitude in the rule's own unit.
 *
 * Currency writes tight millions ("€21M") to match the design copy, but only
 * once there is a million to write. `formatMillionsTight` rounds to whole
 * millions because it was written for transfer fees; a rule deviation has no
 * such floor, and a club €400k over its wage cap would render as "€0M over the
 * wage budget" — a breach whose stated size is zero. Below a million, defer to
 * the app-wide short formatter, which scales down to "€400K".
 *
 * A percentage magnitude here is a difference of two percentages, so it is
 * written in points rather than with a `%` that would read as a share of the
 * limit.
 * @param magnitude - The absolute magnitude.
 * @param unit - What the rule is denominated in.
 * @returns The formatted magnitude.
 */
export const formatMagnitude = (
  magnitude: number,
  unit: ComplianceQuantityUnit
): string => {
  switch (unit) {
    case ComplianceQuantityUnit.Currency:
      return magnitude < ONE_MILLION
        ? formatCurrencyShort(magnitude, CurrencyCode.Eur)
        : formatMillionsTight(magnitude);
    case ComplianceQuantityUnit.Percentage:
      return `${trimmedOf(magnitude)} pts`;
    case ComplianceQuantityUnit.Years:
      return `${trimmedOf(magnitude)} ${magnitude === 1 ? "year" : "years"}`;
    case ComplianceQuantityUnit.Count:
      return String(Math.round(magnitude));
  }
};

/**
 * A rule's own reading, in its own unit.
 *
 * Distinct from {@link formatMagnitude} in two ways, both load bearing.
 *
 * A magnitude is a DIFFERENCE between two figures, so a percentage becomes
 * "points"; a reading is a LEVEL, so it keeps its `%`. Rendering 88.4% as
 * "88.4 pts" beside a 70% cap is exactly what the points suffix exists to
 * prevent.
 *
 * And a magnitude is unsigned by contract — callers hand it `Math.abs` and the
 * direction is written as a word — whereas a reading's SIGN is part of the
 * reading. `formatMillionsTight` drops it (it takes `Math.abs` internally,
 * correctly, for its own magnitude purpose), so this composes the sign back
 * on. Without that, a PSR rolling loss of −€60M renders identically to a €60M
 * profit — and PSR is denominated so that negative means loss.
 * @param value - The reading.
 * @param unit - What the rule is denominated in.
 * @returns The formatted reading.
 */
export const formatReading = (
  value: number,
  unit: ComplianceQuantityUnit
): string => {
  switch (unit) {
    case ComplianceQuantityUnit.Currency: {
      const magnitude = Math.abs(value);
      const written =
        magnitude < ONE_MILLION
          ? formatCurrencyShort(magnitude, CurrencyCode.Eur)
          : formatMillionsTight(magnitude);
      return value < 0 ? `-${written}` : written;
    }
    case ComplianceQuantityUnit.Percentage:
      return `${trimmedOf(value)}%`;
    case ComplianceQuantityUnit.Years:
      return `${trimmedOf(value)} ${value === 1 ? "year" : "years"}`;
    case ComplianceQuantityUnit.Count:
      return String(Math.round(value));
  }
};
